import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { StationPhoto as Photo } from '../api/types'
import { usePolled } from '../lib/usePolled'
import { mergePhotos, photoAgeNote, stepPhoto } from '../lib/photos'
import { formatDateTime, relativeAge } from '../lib/time'

/** Five minutes: the logger uploads hourly, so this only keeps the age honest
 *  and picks a new photo up within a few minutes of its arrival. */
const REFRESH_MS = 5 * 60_000
const PAGE = 12

interface Props {
  code: string
}

/** The station's camera photo, newest first, with Older / Newer to step back
 *  through earlier ones (an MRL push station's second data channel; backend
 *  ingest/mrl_photos.py). The time shown is when the photo arrived: the
 *  JPEG carries no time of its own. */
export function StationPhoto({ code }: Props) {
  const latest = usePolled(() => api.photos(code, undefined, PAGE), [code], REFRESH_MS)
  const [older, setOlder] = useState<Photo[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [exhausted, setExhausted] = useState(false)
  const [pageError, setPageError] = useState<string>()

  // A different station is a different set of photos.
  useEffect(() => {
    setOlder([])
    setSelected(null)
    setExhausted(false)
    setPageError(undefined)
  }, [code])

  const photos = mergePhotos(latest.data ?? [], older)
  const current = stepPhoto(photos, selected, 0).id
  const shown = photos.find((p) => p.id === current)
  const at = shown ? photos.indexOf(shown) : -1

  const goOlder = async () => {
    const step = stepPhoto(photos, selected, 1)
    if (!step.needMore) {
      setSelected(step.id)
      return
    }
    const oldest = photos[photos.length - 1]
    if (!oldest) return
    try {
      const page = await api.photos(code, oldest.id, PAGE)
      const first = page[0]
      if (!first) {
        setExhausted(true)
        return
      }
      setOlder((prev) => mergePhotos(prev, page))
      setSelected(first.id)
      setPageError(undefined)
    } catch (error) {
      setPageError(error instanceof Error ? error.message : String(error))
    }
  }

  const goNewer = () => {
    const step = stepPhoto(photos, selected, -1)
    setSelected(step.id === photos[0]?.id ? null : step.id)
  }

  const newest = photos[0]
  const ageNote = newest ? photoAgeNote(newest.received_at) : null

  return (
    <section className="live station-photo">
      <header>
        <h2>Station Photo</h2>
        <p className="muted">
          {shown
            ? `received ${formatDateTime(shown.received_at)} · ${relativeAge(shown.received_at)}`
            : ''}
        </p>
      </header>

      {latest.error && !photos.length ? (
        <p className="muted">Could not load the photos ({latest.error}).</p>
      ) : null}

      {!latest.loading && !latest.error && !photos.length ? (
        <p className="muted">No photos received from this station yet.</p>
      ) : null}

      {ageNote && at === 0 ? <p className="notice notice-warn">{ageNote}</p> : null}

      {shown ? (
        <figure>
          <img src={api.photoUrl(code, shown.id)}
               alt={`Station camera, received ${formatDateTime(shown.received_at)}`} />
        </figure>
      ) : null}

      {photos.length ? (
        <div className="photo-nav">
          <button type="button" className="button" onClick={goOlder}
                  disabled={exhausted && at === photos.length - 1}>
            ‹ Older
          </button>
          <span className="muted">
            {at === 0 ? 'newest' : `${at} newer ${at === 1 ? 'photo' : 'photos'}`}
          </span>
          <button type="button" className="button" onClick={goNewer} disabled={at <= 0}>
            Newer ›
          </button>
        </div>
      ) : null}

      {pageError ? <p className="muted">Could not load older photos ({pageError}).</p> : null}
    </section>
  )
}
